import { useSelectedOrg } from "@fero/hooks";
import {
  Card,
  Stack,
  Table,
  TableBody,
  TableContainer,
  type Theme,
  useMediaQuery,
} from "@mui/material";
import type {
  RoundedDeliveryFee,
  SaleOrderTotalsView,
} from "adl-gen/ferovinum/app/api";
import type { Currency } from "adl-gen/ferovinum/app/db";
import {
  DividerRow,
  FinancialValueRow,
  FinancialValueRowWithCustomValue,
} from "components/library/widgets/financial-value-table-widgets";
import { TaxAndDeliveryRechargeNotice } from "components/widgets/tax-and-delivery-recharge-notice";
import { ensure } from "utils/null-and-undefined-utils";

type RepurchaseProductsSummaryProps = SaleOrderTotalsView & {
  settlementCurrency: Currency;
  usingCredit: boolean;
  rechargeTaxes: boolean;
  rechargeDeliveryFee: boolean;
};

export const RepurchaseProductsSummary = ({
  settlementCurrency,
  netSubtotal,
  fulfilmentFee,
  duty,
  vat,
  deliveryFee,
  creditFees,
  usingCredit,
  totalWithVat,
  rechargeTaxes,
  rechargeDeliveryFee,
}: RepurchaseProductsSummaryProps) => {
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("md"),
  );
  const hasVat =
    vat !== null ||
    duty !== null ||
    deliveryFee !== null ||
    fulfilmentFee !== null;
  const hasRecharges = rechargeTaxes || rechargeDeliveryFee;
  const organisation = ensure(useSelectedOrg());
  return (
    <Stack spacing={4}>
      <Card
        sx={{
          backgroundColor: "common.grey1",
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        }}
      >
        <TableContainer sx={{ overflowX: "hidden" }}>
          <Table stickyHeader={false} sx={{ backgroundColor: "common.grey1" }}>
            <colgroup>
              <col style={{ width: isSmallScreen ? "50%" : "85%" }} />
              <col style={{ width: isSmallScreen ? "50%" : "15%" }} />
            </colgroup>
            <TableBody>
              <FinancialValueRow
                title={hasVat ? "Net subtotal" : "Total"}
                value={netSubtotal}
                currency={settlementCurrency}
              />

              <DividerRow />

              <TaxesSection
                usingCredit={usingCredit}
                creditFee={creditFees}
                rechargeTaxes={rechargeTaxes}
                duty={duty}
                vat={vat}
                fulfilmentFee={fulfilmentFee}
                currency={settlementCurrency}
              />

              <DeliverySection
                rechargeDeliveryFee={rechargeDeliveryFee}
                deliveryFee={deliveryFee}
                currency={settlementCurrency}
              />

              <FinancialValueRow
                title={hasVat ? "Total" : "Total with VAT"}
                value={totalWithVat}
                currency={settlementCurrency}
              />
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {hasRecharges && (
        <TaxAndDeliveryRechargeNotice
          businessName={organisation.value.businessName}
        />
      )}
    </Stack>
  );
};

const TaxesSection = ({
  usingCredit,
  creditFee,
  rechargeTaxes,
  fulfilmentFee,
  duty,
  vat,
  currency,
}: {
  usingCredit: boolean;
  creditFee: string | null;
  rechargeTaxes: boolean;
  fulfilmentFee: string | null;
  duty: string | null;
  vat: string | null;
  currency: Currency;
}) => {
  const showFulfilmentFee = fulfilmentFee !== null && Number(fulfilmentFee) > 0;
  const showCreditFee =
    usingCredit && creditFee !== null && Number(creditFee) > 0;
  if (rechargeTaxes) {
    return (
      <>
        {showCreditFee && (
          <FinancialValueRow
            title="Credit Fee"
            value={creditFee}
            currency={currency}
          />
        )}
        {showFulfilmentFee && (
          <FinancialValueRow
            title="Fulfilment Fee"
            value={fulfilmentFee}
            currency={currency}
          />
        )}
        <FinancialValueRowWithCustomValue title="Duty" valueNode={"n/a**"} />
        <FinancialValueRowWithCustomValue title="VAT" valueNode={"n/a**"} />
        <DividerRow />
      </>
    );
  }

  if (duty == null && vat == null && fulfilmentFee == null) {
    return null;
  }

  return (
    <>
      {showFulfilmentFee && (
        <FinancialValueRow
          title="Fulfilment Fee"
          value={fulfilmentFee}
          currency={currency}
        />
      )}
      {duty !== null && (
        <FinancialValueRow title="Duty" value={duty} currency={currency} />
      )}
      {vat !== null && (
        <FinancialValueRow title="VAT" value={vat} currency={currency} />
      )}
      <DividerRow />
    </>
  );
};

const DeliverySection = ({
  rechargeDeliveryFee,
  deliveryFee,
  currency,
}: {
  rechargeDeliveryFee: boolean;
  deliveryFee: RoundedDeliveryFee | null;
  currency: Currency;
}) => {
  if (rechargeDeliveryFee) {
    return (
      <>
        <FinancialValueRowWithCustomValue
          title="Delivery Fee"
          valueNode={"n/a**"}
        />
        <FinancialValueRowWithCustomValue title="VAT" valueNode={"n/a**"} />
        <DividerRow />
      </>
    );
  }

  if (!deliveryFee) {
    return null;
  }

  return (
    <>
      <FinancialValueRow
        title="Delivery Fee"
        value={deliveryFee.totalDeliveryFee}
        currency={currency}
      />
      <FinancialValueRow
        title="VAT"
        value={deliveryFee.vat}
        currency={currency}
      />
      <DividerRow />
    </>
  );
};
